import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  TablePagination,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  useTheme,
  Menu,
  MenuItem,
  Alert,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';

const ActionQueuePage = () => {
  const theme = useTheme();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterActionRequired, setFilterActionRequired] = useState('all');
  const [filterTaskDetail, setFilterTaskDetail] = useState('all');
  const [filterTaskType, setFilterTaskType] = useState('all');
  const [filterExecuted, setFilterExecuted] = useState('all');
  const [filterFixed, setFilterFixed] = useState('all');
  const [filterWorkflowExecuted, setFilterWorkflowExecuted] = useState('all');
  const [fixedModalOpen, setFixedModalOpen] = useState(false);
  const [workflowExecutedModalOpen, setWorkflowExecutedModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [objectIdModalOpen, setObjectIdModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [timestampMenuAnchor, setTimestampMenuAnchor] = useState(null);
  const [selectedTaskForTimestamp, setSelectedTaskForTimestamp] = useState(null);
  const [selectedTimestamps, setSelectedTimestamps] = useState({});
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [createTaskLoading, setCreateTaskLoading] = useState(false);
  const [createTaskError, setCreateTaskError] = useState(null);
  const [createTaskData, setCreateTaskData] = useState({
    businessName: '',
    googleLocationId: '',
    taskType: '',
    taskDetail: '',
    executionDetail: '',
    taskExecutionTimestamp: '',
    executedStatus: 'Action Required'
  });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/action-queue-tasks');
      setTasks(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching action queue tasks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const uniqueTaskDetails = [...new Set(tasks.map(t => t.taskDetail))].sort();
  const uniqueTaskTypes = [...new Set(tasks.map(t => t.taskType))].sort();

  const filterTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      backgroundColor: '#f9f9f9',
      transition: 'all 0.2s ease',
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    }
  };

  const filteredTasks = tasks.filter(task => {
    // Get the latest timestamp (sorted by taskExecutionTimestamp descending)
    const sortedTasks = task.tasks ? [...task.tasks].sort((a, b) => {
      const dateA = new Date(a.taskExecutionTimestamp || 0).getTime();
      const dateB = new Date(b.taskExecutionTimestamp || 0).getTime();
      return dateB - dateA;
    }) : [];
    const latestTaskData = sortedTasks.length > 0 ? sortedTasks[0] : {};
    const searchMatch = task.businessName.toLowerCase().includes(filterSearch.toLowerCase()) ||
      task.googleLocationId.includes(filterSearch);
    const actionMatch = filterActionRequired === 'all' ||
      (filterActionRequired === 'true' ? latestTaskData.ActionRequired : !latestTaskData.ActionRequired);
    const detailMatch = filterTaskDetail === 'all' || task.taskDetail === filterTaskDetail;
    const typeMatch = filterTaskType === 'all' || task.taskType === filterTaskType;
    const executedMatch = filterExecuted === 'all' ||
      (filterExecuted === 'true' ? latestTaskData.taskExecuted : !latestTaskData.taskExecuted);
    const fixedMatch = filterFixed === 'all' ||
      (filterFixed === 'true' ? task.fixed : !task.fixed) ||
      (filterFixed === 'n/a' ? !latestTaskData.ActionRequired : false);
    const workflowExecutedMatch = filterWorkflowExecuted === 'all' ||
      (filterWorkflowExecuted === 'true' ? latestTaskData.workflowExecuted : !latestTaskData.workflowExecuted);
    return searchMatch && actionMatch && detailMatch && typeMatch && executedMatch && fixedMatch && workflowExecutedMatch;
  });

  const paginatedTasks = filteredTasks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getActionRequiredColor = (actionRequired) => {
    return actionRequired ? 'error' : 'success';
  };

  const getTaskTypeColor = (taskType) => {
    return 'default';
  };

  const getTaskTypeChipSx = (taskType) => {
    const colorMap = {
      'Keywords': {
        backgroundColor: '#2196F3',
        color: '#fff',
        borderColor: '#2196F3'
      },
      'Posts': {
        backgroundColor: '#4CAF50',
        color: '#fff',
        borderColor: '#4CAF50'
      },
      'Service': {
        backgroundColor: '#FF9800',
        color: '#fff',
        borderColor: '#FF9800'
      },
      'Reviews': {
        backgroundColor: '#FF6B6B',
        color: '#fff',
        borderColor: '#FF6B6B'
      },
      'Profile': {
        backgroundColor: '#1A237E',
        color: '#fff',
        borderColor: '#1A237E'
      },
      'Photos': {
        backgroundColor: '#1A237E',
        color: '#fff',
        borderColor: '#1A237E'
      },
      'Media': {
        backgroundColor: '#9C27B0',
        color: '#fff',
        borderColor: '#9C27B0'
      }
    };

    const style = colorMap[taskType] || {};
    return {
      ...style,
      '& .MuiChip-label': {
        color: '#fff'
      }
    };
  };

  const getTaskDetailColor = (taskDetail) => {
    if (taskDetail.includes('No')) return 'warning';
    if (taskDetail.includes('Analysis')) return 'info';
    if (taskDetail.includes('Progress')) return 'secondary';
    return 'default';
  };

  const formatTimestampWithColors = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString();
    return { dateStr, timeStr };
  };

  const handleFixedClick = (task) => {
    setSelectedTask(task);
    setFixedModalOpen(true);
  };

  const handleFixedConfirm = async () => {
    if (!selectedTask) return;

    try {
      console.log('🔄 Updating task:', selectedTask._id);
      const response = await axios.patch(`/api/action-queue-tasks/${selectedTask._id}`, {
        fixed: true
      });

      console.log('✅ Task updated successfully:', response.data);

      setTasks(tasks.map(task =>
        task._id === selectedTask._id ? { ...task, fixed: true } : task
      ));

      setFixedModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('❌ Error updating task:', err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update task';
      alert(`Failed to update task: ${errorMessage}`);
    }
  };

  const handleFixedCancel = () => {
    setFixedModalOpen(false);
    setSelectedTask(null);
  };

  const handleWorkflowExecutedClick = (task) => {
    setSelectedTask(task);
    setWorkflowExecutedModalOpen(true);
  };

  const handleWorkflowExecutedConfirm = async (newValue) => {
    if (!selectedTask) return;

    try {
      console.log('🔄 Updating task:', selectedTask._id, 'workflowExecuted:', newValue);
      const response = await axios.patch(`/api/action-queue-tasks/${selectedTask._id}`, {
        workflowExecuted: newValue
      });

      console.log('✅ Task updated successfully:', response.data);

      setTasks(tasks.map(task =>
        task._id === selectedTask._id ? { ...task, workflowExecuted: newValue } : task
      ));

      setWorkflowExecutedModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('❌ Error updating task:', err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update task';
      alert(`Failed to update task: ${errorMessage}`);
    }
  };

  const handleWorkflowExecutedCancel = () => {
    setWorkflowExecutedModalOpen(false);
    setSelectedTask(null);
  };

  const handleDeleteClick = (task) => {
    setSelectedTask(task);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTask) return;

    try {
      console.log('🗑️ Deleting task:', selectedTask._id);
      await axios.delete(`/api/action-queue-tasks/${selectedTask._id}`);

      console.log('✅ Task deleted successfully');

      setTasks(tasks.filter(task => task._id !== selectedTask._id));
      setDeleteModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('❌ Error deleting task:', err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete task';
      alert(`Failed to delete task: ${errorMessage}`);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedTask(null);
  };

  const handleObjectIdClick = (objectId) => {
    setSelectedObjectId(objectId);
    setObjectIdModalOpen(true);
  };

  const handleObjectIdCancel = () => {
    setObjectIdModalOpen(false);
    setSelectedObjectId(null);
  };

  const handleTimestampMenuOpen = (event, task) => {
    setTimestampMenuAnchor(event.currentTarget);
    setSelectedTaskForTimestamp(task);
  };

  const handleTimestampMenuClose = () => {
    setTimestampMenuAnchor(null);
    setSelectedTaskForTimestamp(null);
  };

  const handleTimestampSelect = (timestamp) => {
    if (selectedTaskForTimestamp?._id) {
      setSelectedTimestamps(prev => ({
        ...prev,
        [selectedTaskForTimestamp._id]: timestamp
      }));
    }
    handleTimestampMenuClose();
  };

  const handleCopyObjectId = () => {
    if (selectedObjectId) {
      navigator.clipboard.writeText(selectedObjectId);
      alert('ObjectId copied to clipboard!');
    }
  };

  const handleOpenCreateTaskModal = () => {
    setCreateTaskData({
      businessName: '',
      googleLocationId: '',
      taskType: '',
      taskDetail: '',
      executionDetail: '',
      taskExecutionTimestamp: '',
      executedStatus: 'Action Required'
    });
    setCreateTaskError(null);
    setCreateTaskModalOpen(true);
  };

  const handleCloseCreateTaskModal = () => {
    setCreateTaskModalOpen(false);
    setCreateTaskError(null);
  };

  const handleCreateTaskSubmit = async () => {
    try {
      setCreateTaskLoading(true);
      setCreateTaskError(null);

      // Validate required fields
      if (!createTaskData.businessName || !createTaskData.googleLocationId || !createTaskData.taskType || !createTaskData.executionDetail || !createTaskData.taskExecutionTimestamp) {
        setCreateTaskError('Please fill in all required fields');
        setCreateTaskLoading(false);
        return;
      }

      // Map executed status to task fields
      const taskFields = {
        taskExecuted: false,
        ActionRequired: false,
        fixed: false,
        workflowExecuted: false
      };

      if (createTaskData.executedStatus === 'Action Required') {
        taskFields.ActionRequired = true;
      } else if (createTaskData.executedStatus === 'Fixed') {
        taskFields.fixed = true;
      } else if (createTaskData.executedStatus === 'Workflow Executed') {
        taskFields.workflowExecuted = true;
      } else if (createTaskData.executedStatus === 'Created') {
        taskFields.taskExecuted = true;
      }

      const payload = {
        businessName: createTaskData.businessName,
        googleLocationId: createTaskData.googleLocationId,
        taskType: createTaskData.taskType,
        taskDetail: createTaskData.taskDetail || createTaskData.taskType,
        tasks: [
          {
            executionDetail: createTaskData.executionDetail,
            taskExecutionTimestamp: createTaskData.taskExecutionTimestamp,
            ...taskFields
          }
        ]
      };

      const response = await axios.post('/api/action-queue-tasks', payload);

      console.log('✅ Task created successfully:', response.data);

      // Close modal and refresh tasks
      setCreateTaskModalOpen(false);
      fetchTasks();
    } catch (err) {
      console.error('❌ Error creating task:', err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create task';
      setCreateTaskError(errorMessage);
    } finally {
      setCreateTaskLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
   <Box sx={{ p: 3, background: 'linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5 }}>
            Action Queue Tasks
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage and track all automated workflow tasks
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenCreateTaskModal}
            variant="contained"
            color="success"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              boxShadow: `0 4px 15px rgba(76, 175, 80, 0.4)`,
              '&:hover': {
                boxShadow: `0 6px 20px rgba(76, 175, 80, 0.6)`,
              }
            }}
          >
            Create Task
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchTasks}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              boxShadow: `0 4px 15px ${theme.palette.primary.main}40`,
              '&:hover': {
                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
              }
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Box mb={2} sx={{
        p: 2.5,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 2,
      }}>
        <TextField
          label="Search Business or Location ID"
          value={filterSearch}
          onChange={(e) => {
            setFilterSearch(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: '100%', ...filterTextFieldSx }}
          placeholder="e.g., Business name or 17626097026949586598"
        />
        <TextField
          select
          label="Filter by Task Type"
          value={filterTaskType}
          onChange={(e) => {
            setFilterTaskType(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: '100%', ...filterTextFieldSx }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="all">All Task Types</option>
          {uniqueTaskTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </TextField>
        <TextField
          select
          label="Filter by Task Detail"
          value={filterTaskDetail}
          onChange={(e) => {
            setFilterTaskDetail(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: '100%', ...filterTextFieldSx }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="all">All Task Details</option>
          {uniqueTaskDetails.map((detail) => (
            <option key={detail} value={detail}>{detail}</option>
          ))}
        </TextField>
        <TextField
          select
          label="Filter by Executed"
          value={filterExecuted}
          onChange={(e) => {
            setFilterExecuted(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: '100%', ...filterTextFieldSx }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="all">All</option>
          <option value="true">Executed</option>
          <option value="false">Not Executed</option>
        </TextField>
        <TextField
          select
          label="Filter by Action Required"
          value={filterActionRequired}
          onChange={(e) => {
            setFilterActionRequired(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 200, ...filterTextFieldSx }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="all">All</option>
          <option value="true">Action Required</option>
          <option value="false">No Action Required</option>
        </TextField>
        <TextField
          select
          label="Filter by Fixed"
          value={filterFixed}
          onChange={(e) => {
            setFilterFixed(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: '100%', ...filterTextFieldSx }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="all">All</option>
          <option value="true">Fixed</option>
          <option value="false">Not Fixed</option>
          <option value="n/a">Not Applicable</option>
        </TextField>
        <TextField
          select
          label="Filter by Workflow Executed"
          value={filterWorkflowExecuted}
          onChange={(e) => {
            setFilterWorkflowExecuted(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: '100%', ...filterTextFieldSx }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="all">All</option>
          <option value="true">Workflow Executed</option>
          <option value="false">Not Executed</option>
        </TextField>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
          Showing <strong style={{ color: theme.palette.primary.main }}>{paginatedTasks.length}</strong> of <strong>{filteredTasks.length}</strong> tasks (Total: <strong>{tasks.length}</strong>)
        </Typography>
      </Box>

      {error && (
        <Box sx={{
          backgroundColor: '#ffebee',
          border: '1px solid #ef5350',
          borderRadius: 2,
          p: 2,
          mb: 2
        }}>
          <Typography color="error" sx={{ fontWeight: 500 }}>
            Error: {error}
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper} sx={{
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: 2,
        maxHeight: 'calc(100vh - 350px)'
      }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{
            backgroundColor: theme.palette.primary.main,
            '& .MuiTableCell-head': {
              color: 'white',
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              padding: '8px 12px',
              borderBottom: 'none',
              backgroundColor: theme.palette.primary.main,
              position: 'sticky',
              top: 0,
              zIndex: 10
            }
          }}>
            <TableRow>
              <TableCell><strong>ObjectId</strong></TableCell>
              <TableCell><strong>Business Name</strong></TableCell>
              <TableCell><strong>Location ID</strong></TableCell>
              <TableCell><strong>Task Type</strong></TableCell>
              <TableCell><strong>Task Detail</strong></TableCell>
              <TableCell><strong>Execution Detail</strong></TableCell>
              <TableCell><strong>Execution Timestamp</strong></TableCell>
              <TableCell align="center"><strong>Executed</strong></TableCell>
              <TableCell align="center"><strong>Action Required</strong></TableCell>
              <TableCell align="center"><strong>Fixed</strong></TableCell>
              <TableCell align="center"><strong>Workflow Executed</strong></TableCell>
              <TableCell><strong>Created At</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTasks.length > 0 ? (
              paginatedTasks.map((task, index) => {
                // Get the selected timestamp or latest timestamp (sorted by taskExecutionTimestamp descending)
                const sortedTasks = task.tasks ? [...task.tasks].sort((a, b) => {
                  const dateA = new Date(a.taskExecutionTimestamp || 0).getTime();
                  const dateB = new Date(b.taskExecutionTimestamp || 0).getTime();
                  return dateB - dateA;
                }) : [];

                let latestTaskData = sortedTasks.length > 0 ? sortedTasks[0] : {};

                // If a specific timestamp is selected for this task, use that instead
                if (selectedTimestamps[task._id]) {
                  const selectedTask = task.tasks?.find(t => t.taskExecutionTimestamp === selectedTimestamps[task._id]);
                  if (selectedTask) {
                    latestTaskData = selectedTask;
                  }
                }
                return (
                <TableRow key={index} hover sx={{
                  backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                  '&:hover': {
                    backgroundColor: '#f0f4ff !important',
                  },
                  transition: 'background-color 0.2s ease',
                  '& .MuiTableCell-body': {
                    padding: '14px 12px',
                    borderBottom: '1px solid #e8eef5',
                  }
                }}>
                  <TableCell align="center">
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={() => handleObjectIdClick(task._id)}
                      title="View complete ObjectId"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: `${theme.palette.primary.main}15`,
                        }
                      }}
                    >
                      ID
                    </Button>
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 600,
                    color: '#1a237e',
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {task.businessName}
                  </TableCell>
                  <TableCell sx={{
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                    color: '#666',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 1,
                    p: '8px 12px',
                    border: '1px solid #e0e0e0'
                  }}>
                    {task.googleLocationId}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.taskType}
                      size="small"
                      color={getTaskTypeColor(task.taskType)}
                      variant="filled"
                      sx={{
                        ...getTaskTypeChipSx(task.taskType),
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.taskDetail}
                      size="small"
                      color={getTaskDetailColor(task.taskDetail)}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderWidth: 2,
                        '& .MuiChip-label': {
                          paddingLeft: 1.5,
                          paddingRight: 1.5,
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{
                    fontSize: '0.85rem',
                    minWidth: 350,
                    color: '#555',
                    fontWeight: 500,
                    backgroundColor: '#f5f5f5',
                    p: '12px',
                    borderRadius: 1,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    lineHeight: 1.5
                  }}>
                    {latestTaskData.executionDetail}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      onClick={(e) => handleTimestampMenuOpen(e, task)}
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#f0f0f0',
                        }
                      }}
                    >
                      {latestTaskData.taskExecutionTimestamp ? (
                        <>
                          <span style={{ color: '#D32F2F', fontWeight: 700 }}>
                            {formatTimestampWithColors(latestTaskData.taskExecutionTimestamp).dateStr}
                          </span>
                          <span style={{ margin: '0 4px', color: '#999' }}>•</span>
                          <span style={{ color: '#1976D2', fontWeight: 700 }}>
                            {formatTimestampWithColors(latestTaskData.taskExecutionTimestamp).timeStr}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={latestTaskData.taskExecuted ? '✓ Yes' : '✗ No'}
                      size="small"
                      variant="filled"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        backgroundColor: latestTaskData.taskExecuted ? '#4CAF50' : '#C62828',
                        boxShadow: latestTaskData.taskExecuted ? '0 2px 8px rgba(76, 175, 80, 0.3)' : '0 2px 8px rgba(198, 40, 40, 0.3)',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={latestTaskData.ActionRequired ? '! Yes' : '✓ No'}
                      size="small"
                      variant="filled"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        backgroundColor: latestTaskData.ActionRequired ? '#C62828' : '#4CAF50',
                        boxShadow: latestTaskData.ActionRequired ? '0 2px 8px rgba(198, 40, 40, 0.3)' : '0 2px 8px rgba(76, 175, 80, 0.3)',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {latestTaskData.ActionRequired ? (
                      task.fixed ? (
                        <Chip
                          label="✓ Fixed"
                          size="small"
                          variant="filled"
                          sx={{
                            fontWeight: 700,
                            color: 'white',
                            backgroundColor: '#81C784',
                            boxShadow: '0 2px 8px rgba(129, 199, 132, 0.3)',
                          }}
                        />
                      ) : (
                        <Chip
                          label="□ Pending"
                          size="small"
                          variant="filled"
                          onClick={() => handleFixedClick(task)}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 700,
                            color: 'white',
                            backgroundColor: theme.palette.primary.main,
                            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${theme.palette.primary.main}60`,
                            }
                          }}
                        />
                      )
                    ) : (
                      <Chip
                        label="N/A"
                        size="small"
                        variant="filled"
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          backgroundColor: '#9C27B0',
                          boxShadow: '0 2px 8px rgba(156, 39, 176, 0.3)',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={latestTaskData.workflowExecuted ? '✓ Yes' : '✗ No'}
                      size="small"
                      variant="filled"
                      onClick={() => handleWorkflowExecutedClick(task)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: 'white',
                        backgroundColor: latestTaskData.workflowExecuted ? '#4CAF50' : '#616161',
                        boxShadow: latestTaskData.workflowExecuted ? '0 2px 8px rgba(76, 175, 80, 0.3)' : `0 2px 8px rgba(97, 97, 97, 0.3)`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{
                    fontSize: '0.85rem',
                    color: '#666',
                    fontWeight: 500,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 1,
                    p: '8px 12px'
                  }}>
                    {new Date(task.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(task)}
                      sx={{
                        fontWeight: 600,
                        borderColor: '#ef5350',
                        color: '#ef5350',
                        '&:hover': {
                          backgroundColor: 'rgba(239, 83, 80, 0.1)',
                          borderColor: '#ef5350',
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    📭 No tasks found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filters or check back later
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{
        backgroundColor: 'white',
        borderTop: '1px solid #e8eef5',
        borderRadius: '0 0 8px 8px',
      }}>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200, 500]}
          component="div"
          count={filteredTasks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '& .MuiTablePagination-root': {
              borderTop: 'none',
            },
            '& .MuiTablePagination-toolbar': {
              paddingRight: 2,
              '& .MuiIconButton-root': {
                color: '#667eea',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                }
              }
            },
            '& .MuiSelect-root': {
              color: '#667eea',
              fontWeight: 600,
            }
          }}
        />
      </Box>

      <Menu
        anchorEl={timestampMenuAnchor}
        open={Boolean(timestampMenuAnchor)}
        onClose={handleTimestampMenuClose}
        PaperProps={{
          sx: {
            maxHeight: '300px',
            width: '350px',
          }
        }}
      >
        {selectedTaskForTimestamp?.tasks && selectedTaskForTimestamp.tasks.length > 0 ? (
          selectedTaskForTimestamp.tasks.map((task, idx) => {
            const { dateStr, timeStr } = formatTimestampWithColors(task.taskExecutionTimestamp);
            return (
              <MenuItem
                key={idx}
                onClick={() => handleTimestampSelect(task.taskExecutionTimestamp)}
                sx={{
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
              >
                {task.taskExecutionTimestamp ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ color: '#D32F2F', fontWeight: 700, fontSize: '0.9rem' }}>
                      {dateStr}
                    </span>
                    <span style={{ color: '#999' }}>•</span>
                    <span style={{ color: '#1976D2', fontWeight: 700, fontSize: '0.9rem' }}>
                      {timeStr}
                    </span>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Task {idx + 1}: No timestamp
                  </Typography>
                )}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              No timestamps available
            </Typography>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={fixedModalOpen} onClose={handleFixedCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          fontWeight: 700,
          fontSize: '1.2rem'
        }}>
          ✓ Mark as Fixed
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
            Is this issue fixed now?
          </Typography>
          {selectedTask && (
            <Box sx={{
              backgroundColor: '#f5f5f5',
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              p: 2,
              borderRadius: 1,
            }}>
              <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>
                Task: <strong>{selectedTask.taskDetail}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Business: <strong>{selectedTask.businessName}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleFixedCancel} variant="outlined" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleFixedConfirm} variant="contained" color="success" sx={{ fontWeight: 600 }}>
            Mark as Fixed
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={workflowExecutedModalOpen} onClose={handleWorkflowExecutedCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          fontWeight: 700,
          fontSize: '1.2rem'
        }}>
          🔄 Update Workflow Status
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
            Update workflow execution status:
          </Typography>
          {selectedTask && (
            <Box sx={{
              backgroundColor: '#f5f5f5',
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              p: 2,
              borderRadius: 1,
              mb: 2
            }}>
              <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>
                Task: <strong>{selectedTask.taskDetail}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Business: <strong>{selectedTask.businessName}</strong>
              </Typography>
              <Box sx={{
                backgroundColor: selectedTask.workflowExecuted ? '#e8f5e9' : '#ffebee',
                borderRadius: 1,
                p: 1.5,
                mt: 2,
                textAlign: 'center'
              }}>
                <Typography variant="body2" sx={{
                  color: selectedTask.workflowExecuted ? '#2e7d32' : '#c62828',
                  fontWeight: 700
                }}>
                  Current Status: {selectedTask.workflowExecuted ? '✓ Executed' : '✗ Not Executed'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleWorkflowExecutedCancel} variant="outlined" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleWorkflowExecutedConfirm(!selectedTask?.workflowExecuted)}
            variant="contained"
            sx={{
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            Toggle to {selectedTask?.workflowExecuted ? 'Not Executed' : 'Executed'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteModalOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: theme.palette.error.main,
          color: 'white',
          fontWeight: 700,
          fontSize: '1.2rem'
        }}>
          🗑️ Delete Task
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.error.main }}>
            Are you sure you want to delete this task?
          </Typography>
          {selectedTask && (
            <Box sx={{
              backgroundColor: '#ffebee',
              borderLeft: `4px solid ${theme.palette.error.main}`,
              p: 2,
              borderRadius: 1,
              mb: 2
            }}>
              <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>
                Task: <strong>{selectedTask.taskDetail}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Business: <strong>{selectedTask.businessName}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block', fontFamily: 'monospace' }}>
                ID: {selectedTask._id}
              </Typography>
            </Box>
          )}
          <Box sx={{
            backgroundColor: '#ffe0b2',
            borderLeft: '4px solid #ff9800',
            p: 2,
            borderRadius: 1
          }}>
            <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 700 }}>
              ⚠️ This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ fontWeight: 600 }}>
            Delete Task
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={objectIdModalOpen} onClose={handleObjectIdCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          fontWeight: 700,
          fontSize: '1.2rem'
        }}>
          🔍 Complete ObjectId
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{
            p: 2.5,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            fontSize: '0.95rem',
            border: `2px solid ${theme.palette.primary.main}`,
            color: '#333',
            lineHeight: 1.6,
            fontWeight: 500
          }}>
            {selectedObjectId}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCopyObjectId} variant="contained" startIcon={<ContentCopyIcon />} sx={{
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
          }}>
            Copy
          </Button>
          <Button onClick={handleObjectIdCancel} variant="outlined" sx={{ fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createTaskModalOpen} onClose={handleCloseCreateTaskModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: '#4CAF50',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.2rem'
        }}>
          ➕ Create New Action Queue Task
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {createTaskError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {createTaskError}
            </Alert>
          )}
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Business Name *"
              value={createTaskData.businessName}
              onChange={(e) => setCreateTaskData({ ...createTaskData, businessName: e.target.value })}
              placeholder="e.g., Acme Corp"
              disabled={createTaskLoading}
            />

            <TextField
              fullWidth
              label="Business Location ID *"
              value={createTaskData.googleLocationId}
              onChange={(e) => setCreateTaskData({ ...createTaskData, googleLocationId: e.target.value })}
              placeholder="e.g., 17626097026949586598"
              disabled={createTaskLoading}
            />

            <TextField
              fullWidth
              label="Task Type *"
              value={createTaskData.taskType}
              onChange={(e) => setCreateTaskData({ ...createTaskData, taskType: e.target.value })}
              placeholder="e.g., Keywords, Posts, Service, Reviews, Profile, Photos, Media"
              disabled={createTaskLoading}
            />

            <TextField
              fullWidth
              label="Task Detail"
              value={createTaskData.taskDetail}
              onChange={(e) => setCreateTaskData({ ...createTaskData, taskDetail: e.target.value })}
              placeholder="e.g., No Google Profile Image, Analysis In Progress"
              disabled={createTaskLoading}
            />

            <TextField
              fullWidth
              label="Execution Detail *"
              value={createTaskData.executionDetail}
              onChange={(e) => setCreateTaskData({ ...createTaskData, executionDetail: e.target.value })}
              placeholder="Enter execution details..."
              multiline
              rows={3}
              disabled={createTaskLoading}
            />

            <TextField
              fullWidth
              label="Execution Timestamp *"
              type="datetime-local"
              value={createTaskData.taskExecutionTimestamp}
              onChange={(e) => setCreateTaskData({ ...createTaskData, taskExecutionTimestamp: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={createTaskLoading}
            />

            <FormControl fullWidth>
              <InputLabel>Executed Status *</InputLabel>
              <Select
                value={createTaskData.executedStatus}
                label="Executed Status *"
                onChange={(e) => setCreateTaskData({ ...createTaskData, executedStatus: e.target.value })}
                disabled={createTaskLoading}
              >
                <MenuItem value="Action Required">Action Required</MenuItem>
                <MenuItem value="Fixed">Fixed</MenuItem>
                <MenuItem value="Workflow Executed">Workflow Executed</MenuItem>
                <MenuItem value="Created">Created</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCreateTaskModal} variant="outlined" disabled={createTaskLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateTaskSubmit}
            variant="contained"
            color="success"
            disabled={createTaskLoading}
            startIcon={createTaskLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {createTaskLoading ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActionQueuePage;
